/**
 * @fileoverview AR-924 — Derives the "Upload for class" target of an activity from the loaded matrix.
 * The matrix has no task due date, so `defaultDate` stays null and the backend applies its own default.
 */

import type { MatchableStudent } from '../teacherUpload'
import type { TaskTrackMatrix, TaskTrackStudentRow } from '../types'
import type { UploadForClassTarget } from './UploadForClassModal'

type DeliverableCell = TaskTrackStudentRow['activities'][number] | undefined

/** The backend refuses teacher writes on a delivery the student made themselves (422). */
function submittedByStudent(cell: DeliverableCell): boolean {
  return Boolean(cell?.deliverableCompleted) && !cell?.deliverable?.uploadedByTeacher
}

/** How many students could still receive a teacher upload for the activity (0 = badge is pointless). */
export function countUploadableStudents(students: readonly TaskTrackStudentRow[], activityId: string): number {
  const cellOf = (student: TaskTrackStudentRow) => student.activities.find(cell => cell.activityId === activityId)
  return students.filter(student => !submittedByStudent(cellOf(student))).length
}

export function buildUploadForClassTarget(
  matrix: TaskTrackMatrix,
  activityId: string,
  projectName: string,
): UploadForClassTarget | null {
  const activity = matrix.activities.find(item => item.id === activityId)
  if (!activity) return null

  const students: MatchableStudent[] = []
  const excludedNames: string[] = []
  for (const student of matrix.students) {
    const cell = student.activities.find(item => item.activityId === activityId)
    if (submittedByStudent(cell)) excludedNames.push(student.fullName)
    else students.push({ id: student.id, firstName: student.firstName, lastName: student.lastName })
  }

  return {
    activityId,
    activityTitle: activity.title,
    lessonTitle: activity.lessonTitle,
    projectName,
    defaultDate: null,
    students,
    excludedNames,
  }
}
